'use client'

import { Avatar, Button, useIsMobile } from '@chain-zen/ui'
import { useEffect, useState } from 'react'

interface IAvatarsGridProps {
  avatarsPerPage?: number
  onAvatarClickAction: (avatarUrl: string) => void
  size?: 'sm' | 'md' | 'lg'
}

const TOTAL_AVATARS = 239
const MOBILE_AVATARS_PER_PAGE = 6
const DESKTOP_AVATARS_PER_PAGE = 9

export const shuffleArray = (array: number[]) => {
  // Simple deterministic shuffle: Reverse + Modulo-based swap
  const shuffled = [...array]
  for (let i = 0; i < array.length; i++) {
    const swapIndex = (i + 7) % array.length // Example deterministic swap logic
    ;[shuffled[i], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[i]]
  }
  return shuffled
}

interface IAvatarGridContentProps {
  avatarsPerPage: number
  currentIndex: number
  onAvatarClick: (avatarUrl: string) => void
  shuffledIndices: number[]
}

function AvatarGridContent({ currentIndex, avatarsPerPage, shuffledIndices, onAvatarClick }: IAvatarGridContentProps) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-4 place-items-center mx-auto w-full max-w-md">
      {Array.from({ length: avatarsPerPage }).map((_, i) => {
        const arrayIndex = currentIndex * avatarsPerPage + i
        if (arrayIndex >= TOTAL_AVATARS) return null

        const avatarIndex = shuffledIndices[arrayIndex]
        const src = `https://chaine-images.s3.amazonaws.com/default-profile-avatars/${avatarIndex}.png`

        return (
          <div
            key={`avatar-${i}`}
            onClick={() => onAvatarClick(src)}
            className="cursor-pointer w-18 h-18 sm:w-24 sm:h-24 flex items-center justify-center relative
              group hover:scale-105 transition-transform duration-200"
          >
            <div
              className="absolute inset-0 rounded-full transition-all duration-200
              group-hover:ring-2 group-hover:ring-primary group-hover:ring-offset-2
              dark:ring-offset-gray-900 dark:group-hover:ring-primary-400"
            />
            <Avatar
              name={`default-profile-avatars-${avatarIndex}`}
              size="2xl"
              className="sm:h-24 sm:w-24 h-16 w-16"
              variant="basic"
              src={src}
              aria-label={`default-profile-avatars-${avatarIndex}`}
              data-testid={'avatars'}
            />
          </div>
        )
      })}
    </div>
  )
}

export function AvatarsGrid({ onAvatarClickAction }: IAvatarsGridProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const isMobile = useIsMobile()
  const avatarsPerPage = isMobile ? MOBILE_AVATARS_PER_PAGE : DESKTOP_AVATARS_PER_PAGE

  // Generate shuffled indices once using deterministic shuffle logic
  const shuffledIndices = shuffleArray(Array.from({ length: TOTAL_AVATARS }, (_, i) => i + 1))

  // Reset current index when avatarsPerPage changes to avoid showing empty grid
  useEffect(() => {
    setCurrentIndex(0)
  }, [avatarsPerPage])

  const handlePrevious = () => {
    setCurrentIndex(currentIndex - 1)
  }

  const handleNext = () => {
    setCurrentIndex(currentIndex + 1)
  }

  const isLastPage = currentIndex >= Math.ceil(TOTAL_AVATARS / avatarsPerPage) - 1
  const isFirstPage = currentIndex === 0

  return (
    <div className="space-y-4 sm:space-y-6 flex flex-col items-center w-full">
      <AvatarGridContent
        currentIndex={currentIndex}
        avatarsPerPage={avatarsPerPage}
        shuffledIndices={shuffledIndices}
        onAvatarClick={onAvatarClickAction}
      />

      <div className="flex justify-center gap-2 sm:gap-4">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={isFirstPage}
          className="min-w-[80px] sm:min-w-[100px]"
        >
          Previous
        </Button>
        <Button variant="outline" onClick={handleNext} disabled={isLastPage} className="min-w-[80px] sm:min-w-[100px]">
          Next
        </Button>
      </div>
    </div>
  )
}
